<script lang="ts">
  import { resolve } from '$app/paths'
  import { isNonEmpty } from '#utils/text.js'
  import type { Art } from '#types'
  type Props = {
    // Callers pass `artist.artworks`, which Sanity can leave null/absent.
    artworks: Art[] | null | undefined
    title?: string
    showTitle?: boolean
  }

  let { artworks, title = 'Artworks', showTitle = true }: Props = $props()

  const list = $derived(artworks ?? [])

  // Cap what we ask the Sanity CDN to serve. 800px covers grid-cols-4 at
  // retina; the natural 3000-4000px source was costing ~1 MB per image.
  const REQUEST_MAX = 800

  function requestSize(natural: { width: number; height: number } | null | undefined) {
    if (!natural) {
      return { w: REQUEST_MAX, h: REQUEST_MAX }
    }
    const aspect = natural.width / natural.height
    return aspect >= 1
      ? { w: REQUEST_MAX, h: Math.round(REQUEST_MAX / aspect) }
      : { w: Math.round(REQUEST_MAX * aspect), h: REQUEST_MAX }
  }

  function imgUrl(src: string, w: number, h: number) {
    return `${src}?auto=format&q=80&w=${String(w)}&h=${String(h)}`
  }
</script>

{#if list.length > 0}
  {#if showTitle}
    <h2 class="mb-4 text-4xl font-bold text-black">{title}</h2>
  {/if}
  <div class="grid grid-cols-2 items-start gap-x-6 gap-y-18 lg:grid-cols-4">
    {#each list as art (art._id)}
      {@const dims = art.image?.asset?.metadata?.dimensions}
      {@const r = requestSize(dims)}
      {@const imageUrl = art.image?.asset?.url}
      {@const artistName = art.artist?.name}
      <a
        href={resolve('/art/[handle]', { handle: art.handle.current })}
        class="group block transition"
      >
        {#if isNonEmpty(imageUrl)}
          <!-- content-visibility on the figure (not the whole <a>): skips
               layout/paint of off-screen image boxes so a 200-artwork artist
               doesn't render a 60k-px wall at once, while every tile stays in
               the DOM (jump links / find-in-page / SEO see everything). Scoped
               to the figure so the caption's shadow-xl below isn't clipped by
               the paint containment cv:auto implies. When we know the image
               dimensions, aspect-ratio reserves the *proportional* height even
               while skipped, so revealing a tile doesn't shift layout (no CLS).
               Without dims we can't know the ratio — forcing a square would
               clip a portrait — so fall back to a flat intrinsic-size estimate
               for those (the h-auto image still renders at its true ratio). -->
          <figure
            class="overflow-hidden [content-visibility:auto] {dims
              ? ''
              : '[contain-intrinsic-size:auto_400px]'}"
            style={dims ? `aspect-ratio: ${String(r.w)} / ${String(r.h)}` : ''}
          >
            <img
              src={imgUrl(imageUrl, r.w, r.h)}
              alt={art.title}
              width={r.w}
              height={r.h}
              loading="lazy"
              decoding="async"
              referrerpolicy="no-referrer"
              class="h-auto w-full scale-100 shadow-xl transition hover:scale-105"
            />
          </figure>
        {/if}
        <div class="mt-2.5 w-max max-w-full p-2.5 shadow-xl">
          <h3 class="text-lg font-bold text-black lg:text-2xl">{art.title}</h3>
          {#if isNonEmpty(artistName)}
            <p class="text-xs text-black lg:text-base">{artistName}</p>
          {/if}
          <p class="text-xs text-black lg:text-base">
            {(art.yearCreated ?? 0) > 0 ? art.yearCreated : 'Year unknown'}
          </p>
          <p class="text-xs text-black">{art.medium ?? ''}</p>
        </div>
      </a>
    {/each}
  </div>
{:else}
  {#if showTitle}
    <h2 class="mb-4 text-xl font-semibold text-black">{title}</h2>
  {/if}
  <p class="text-black italic">No artworks found.</p>
{/if}
